import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  LifeBuoy,
  Ticket,
  Users,
  LogOut,
  Menu,
  X,
  CircleDot,
  CheckCircle2,
  XCircle,
  PlusCircle,
  Mail,
  Briefcase,
  Calendar,
  User2,
  UserPlus,
  Search,
  Home,
  AlertOctagon,
  Wrench,
  Sparkles,
  HelpCircle,
} from "lucide-react";

const API_BASE = "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api";

const STATUS_OPTIONS = ["New", "Assigned", "InProgress", "Resolved", "Closed"];

const STATUS_STYLES = {
  New: "bg-slate-100 text-slate-700 border border-slate-300",
  Assigned: "bg-blue-100 text-blue-700 border border-blue-300",
  InProgress: "bg-amber-100 text-amber-700 border border-amber-300",
  Resolved: "bg-emerald-100 text-emerald-700 border border-emerald-300",
  Closed: "bg-gray-200 text-gray-600 border border-gray-300",
};

const PRIORITY_BORDER = {
  Low: "border-l-slate-300",
  Medium: "border-l-blue-400",
  High: "border-l-amber-400",
  Critical: "border-l-red-500",
};

const STATUS_LABEL = (s) => (s === "InProgress" ? "In Progress" : s);

const CATEGORY_ICON = {
  SystemDown: <AlertOctagon className="w-3.5 h-3.5" />,
  BugNotWorking: <Wrench className="w-3.5 h-3.5" />,
  FeatureRequest: <Sparkles className="w-3.5 h-3.5" />,
  Other: <HelpCircle className="w-3.5 h-3.5" />,
};

const CATEGORY_LABEL = {
  SystemDown: "System Down",
  BugNotWorking: "Not Working",
  FeatureRequest: "Feature Request",
  Other: "Other",
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.New}`}>
      {STATUS_LABEL(status)}
    </span>
  );
}

export default function MsaSupportAdminDashboard() {
  const navigate = useNavigate();
  const [adminData] = useState(() => JSON.parse(sessionStorage.getItem("msaAdmin") || "null"));

  useEffect(() => {
    if (!adminData) navigate("/MsaAdminLogin");
  }, [adminData, navigate]);

  const [activeTab, setActiveTab] = useState("tickets");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [teamMemberFilter, setTeamMemberFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const TICKETS_PER_PAGE = 5;
  const [assignModalTicket, setAssignModalTicket] = useState(null);
  const [assignTargetId, setAssignTargetId] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Team members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [newMember, setNewMember] = useState({ name: "", email: "", designation: "" });
  const [savingMember, setSavingMember] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const url = statusFilter === "all"
        ? `${API_BASE}/tickets`
        : `${API_BASE}/tickets?status=${statusFilter}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to load tickets (${res.status})`);
      setTickets(await res.json());
    } catch (err) {
      toast.error(err.message || "Failed to load tickets");
    } finally {
      setLoadingTickets(false);
    }
  }, [statusFilter]);

  const fetchTeamMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const res = await fetch(`${API_BASE}/teammembers`);
      if (!res.ok) throw new Error(`Failed to load team members (${res.status})`);
      setTeamMembers(await res.json());
    } catch (err) {
      toast.error(err.message || "Failed to load team members");
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, teamMemberFilter, search]);

  const handleLogout = () => {
    sessionStorage.removeItem("msaAdmin");
    navigate("/MsaAdminLogin");
  };

  const openAssignModal = (ticket) => {
    setAssignModalTicket(ticket);
    setAssignTargetId(ticket.assignedToTeamMemberId ? String(ticket.assignedToTeamMemberId) : "");
  };

  const handleAssign = async () => {
    if (!assignTargetId) {
      toast.error("Choose a team member first");
      return;
    }
    setAssigning(true);
    try {
      const res = await fetch(`${API_BASE}/tickets/${assignModalTicket.ticketId}/assign`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ TeamMemberId: Number(assignTargetId) }),
      });
      if (!res.ok) throw new Error(await res.text() || "Failed to assign ticket");
      toast.success("Ticket assigned - the logger has been notified");
      setAssignModalTicket(null);
      fetchTickets();
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.message || "Failed to assign ticket");
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusChange = async (ticket, newStatus) => {
    try {
      const res = await fetch(`${API_BASE}/tickets/${ticket.ticketId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Status: newStatus }),
      });
      if (!res.ok) throw new Error(await res.text() || "Failed to update status");
      toast.success(`Ticket ${ticket.ticketNumber} marked ${STATUS_LABEL(newStatus)}`);
      fetchTickets();
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!newMember.name.trim() || !newMember.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSavingMember(true);
    try {
      const res = await fetch(`${API_BASE}/teammembers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Name: newMember.name,
          Email: newMember.email,
          Designation: newMember.designation,
        }),
      });
      if (!res.ok) throw new Error(await res.text() || "Failed to add team member");
      toast.success("Team member added");
      setNewMember({ name: "", email: "", designation: "" });
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.message || "Failed to add team member");
    } finally {
      setSavingMember(false);
    }
  };

  const toggleMemberActive = async (member) => {
    try {
      const action = member.isActive ? "deactivate" : "activate";
      const res = await fetch(`${API_BASE}/teammembers/${member.teamMemberId}/${action}`, { method: "PUT" });
      if (!res.ok) throw new Error(`Failed to ${action} team member`);
      toast.success(`${member.name} ${member.isActive ? "deactivated" : "activated"}`);
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredTickets = tickets.filter((t) => {
    // Team member filter
    if (teamMemberFilter === "unassigned" && t.assignedToTeamMemberId) return false;
    if (
      teamMemberFilter !== "all" &&
      teamMemberFilter !== "unassigned" &&
      String(t.assignedToTeamMemberId) !== teamMemberFilter
    ) {
      return false;
    }

    // Search - across every field on the ticket
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      t.ticketNumber.toLowerCase().includes(q) ||
      t.subject.toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q) ||
      (t.category || "").toLowerCase().includes(q) ||
      (t.priority || "").toLowerCase().includes(q) ||
      (t.status || "").toLowerCase().includes(q) ||
      t.loggedByName.toLowerCase().includes(q) ||
      t.loggedByEmail.toLowerCase().includes(q) ||
      (t.residenceName || "").toLowerCase().includes(q) ||
      (t.assignedToTeamMemberName || "").toLowerCase().includes(q) ||
      (t.assignedToTeamMemberDesignation || "").toLowerCase().includes(q) ||
      (t.resolutionNotes || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / TICKETS_PER_PAGE));
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * TICKETS_PER_PAGE,
    currentPage * TICKETS_PER_PAGE
  );

  const counts = {
    total: tickets.length,
    open: tickets.filter((t) => t.status !== "Resolved" && t.status !== "Closed").length,
    unassigned: tickets.filter((t) => !t.assignedToTeamMemberId).length,
    resolved: tickets.filter((t) => t.status === "Resolved" || t.status === "Closed").length,
  };

  const activeMembers = teamMembers.filter((m) => m.isActive);
  const firstName = (adminData?.name || "").split(" ")[0];

  const navItems = [
    { id: "tickets", label: "All Tickets", icon: <Ticket className="w-5 h-5" /> },
    { id: "team", label: "Team Members", icon: <Users className="w-5 h-5" /> },
  ];

  if (!adminData) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md z-40 px-4 py-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
            <LifeBuoy className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-gray-800">MSA Support Admin</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg hover:bg-gray-100 transition">
          {mobileMenuOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-72 bg-gray-900 shadow-xl z-30 transition-transform duration-300 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="px-6 py-8 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <LifeBuoy className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">MSA Support</h1>
                <p className="text-xs text-gray-500">Admin Dashboard</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  activeTab === item.id ? "bg-gray-800 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                }`}
              >
                <span>{item.icon}</span>
                <span className="text-sm font-medium">{item.label}</span>
                {activeTab === item.id && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-800">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
            <p className="text-[10px] text-gray-600 text-center mt-4">Powered by Basic Intelligent Software (BIS)</p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72">
        <div className="p-5 md:p-8 pt-20 lg:pt-8">
          {/* Header Banner */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full -mr-32 -mt-32 opacity-60" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-blue-200 flex-shrink-0">
                  {(adminData?.name || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{getGreeting()}, {firstName}</h1>
                  <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
                    <LifeBuoy className="w-3.5 h-3.5" />
                    MSA Support Admin
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  {counts.unassigned === 0
                    ? "Every ticket is assigned"
                    : `${counts.unassigned} ticket${counts.unassigned === 1 ? "" : "s"} awaiting assignment`}
                </p>
                <p className="text-sm font-semibold text-gray-700">{adminData?.username}</p>
              </div>
            </div>
          </div>

          {/* Tickets Tab */}
          {activeTab === "tickets" && (
            <div className="space-y-6">
              {/* Stat cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 shadow-lg shadow-blue-200 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-100 font-medium">Total Tickets</p>
                      <p className="text-3xl font-bold mt-1">{counts.total}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-xl"><Ticket className="w-6 h-6 text-white" /></div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-5 shadow-lg shadow-amber-200 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-50 font-medium">Open</p>
                      <p className="text-3xl font-bold mt-1">{counts.open}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-xl"><CircleDot className="w-6 h-6 text-white" /></div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-rose-500 to-red-600 rounded-xl p-5 shadow-lg shadow-rose-200 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-rose-100 font-medium">Unassigned</p>
                      <p className="text-3xl font-bold mt-1">{counts.unassigned}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-xl"><XCircle className="w-6 h-6 text-white" /></div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 shadow-lg shadow-emerald-200 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-100 font-medium">Resolved / Closed</p>
                      <p className="text-3xl font-bold mt-1">{counts.resolved}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-xl"><CheckCircle2 className="w-6 h-6 text-white" /></div>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setStatusFilter("all")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${statusFilter === "all" ? "bg-gray-800 text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                    >
                      All
                    </button>
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${statusFilter === s ? "bg-blue-600 text-white shadow-md" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}
                      >
                        {STATUS_LABEL(s)}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                    <select
                      value={teamMemberFilter}
                      onChange={(e) => setTeamMemberFilter(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="all" className="text-gray-900">All team members</option>
                      <option value="unassigned" className="text-gray-900">Unassigned</option>
                      {teamMembers.map((m) => (
                        <option key={m.teamMemberId} value={String(m.teamMemberId)} className="text-gray-900">
                          {m.name}
                        </option>
                      ))}
                    </select>
                    <div className="relative w-full lg:w-72">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search anything on a ticket..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticket list */}
              <div className="flex items-center justify-between px-1">
                <p className="text-sm text-gray-500">
                  {loadingTickets
                    ? "Loading..."
                    : `${filteredTickets.length} ticket${filteredTickets.length === 1 ? "" : "s"}` +
                      (teamMemberFilter === "unassigned"
                        ? " · unassigned"
                        : teamMemberFilter !== "all"
                        ? ` · assigned to ${teamMembers.find((m) => String(m.teamMemberId) === teamMemberFilter)?.name || ""}`
                        : "")}
                </p>
              </div>
              <div className="space-y-3">
                {loadingTickets && <p className="text-sm text-gray-500 px-1">Loading tickets...</p>}

                {!loadingTickets && filteredTickets.length === 0 && (
                  <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Ticket className="w-8 h-8 text-blue-400" />
                    </div>
                    <p className="text-gray-600 font-medium">No tickets found</p>
                    <p className="text-sm text-gray-400 mt-1">Try a different filter or search term</p>
                  </div>
                )}

                {!loadingTickets && paginatedTickets.map((t) => (
                  <div
                    key={t.ticketId}
                    className={`bg-white rounded-xl shadow-md border border-gray-200 border-l-4 ${PRIORITY_BORDER[t.priority] || "border-l-gray-300"} p-5`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className="text-xs font-mono text-gray-400">{t.ticketNumber}</span>
                          <StatusPill status={t.status} />
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                            {CATEGORY_ICON[t.category] || <HelpCircle className="w-3.5 h-3.5" />}
                            {CATEGORY_LABEL[t.category] || t.category}
                          </span>
                          <span className="text-xs font-medium text-gray-500">{t.priority} priority</span>
                        </div>
                        <p className="font-semibold text-gray-900">{t.subject}</p>
                        <p className="text-sm text-gray-600 mt-1">{t.description}</p>

                        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1.5 font-medium text-gray-700">
                            <Home className="w-3.5 h-3.5" />
                            {t.residenceName || "Unknown residence"}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <User2 className="w-3.5 h-3.5" />
                            {t.loggedByName} ({t.loggedByEmail})
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(t.createdDate).toLocaleString()}
                          </span>
                          {t.assignedToTeamMemberName && (
                            <span className="flex items-center gap-1.5 text-blue-600 font-medium">
                              <Briefcase className="w-3.5 h-3.5" />
                              {t.assignedToTeamMemberName} ({t.assignedToTeamMemberDesignation})
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                        <button
                          onClick={() => openAssignModal(t)}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm transition"
                        >
                          <Briefcase className="w-4 h-4" />
                          {t.assignedToTeamMemberId ? "Reassign" : "Assign"}
                        </button>
                        <select
                          value={t.status}
                          onChange={(e) => handleStatusChange(t, e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s} className="text-gray-900">{STATUS_LABEL(s)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {!loadingTickets && filteredTickets.length > TICKETS_PER_PAGE && (
                <div className="flex items-center justify-between px-1 pt-2">
                  <p className="text-xs text-gray-500">
                    Showing {(currentPage - 1) * TICKETS_PER_PAGE + 1}-
                    {Math.min(currentPage * TICKETS_PER_PAGE, filteredTickets.length)} of {filteredTickets.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600 px-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Team Members Tab */}
          {activeTab === "team" && (
            <div className="space-y-6">
              {/* Add member card */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-white to-indigo-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg"><UserPlus className="w-5 h-5 text-blue-600" /></div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Add a team member</h2>
                      <p className="text-xs text-gray-500">They'll be assignable to tickets once added</p>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleCreateMember} className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={newMember.name}
                      onChange={(e) => setNewMember((m) => ({ ...m, name: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Jane Dev"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={newMember.email}
                      onChange={(e) => setNewMember((m) => ({ ...m, email: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="jane@bluwave.co.za"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                    <input
                      type="text"
                      value={newMember.designation}
                      onChange={(e) => setNewMember((m) => ({ ...m, designation: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Developer"
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <button
                      type="submit"
                      disabled={savingMember}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition"
                    >
                      <PlusCircle className="w-4 h-4" />
                      {savingMember ? "Adding..." : "Add team member"}
                    </button>
                  </div>
                </form>
              </div>

              {/* Team list */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg"><Users className="w-5 h-5 text-indigo-600" /></div>
                      <h2 className="text-base font-semibold text-gray-900">Support Team</h2>
                    </div>
                    <span className="text-xs text-gray-600 bg-white px-2.5 py-1 rounded-full border border-gray-200 shadow-sm">
                      {activeMembers.length} Active
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  {loadingMembers && <p className="text-sm text-gray-500">Loading team members...</p>}

                  {!loadingMembers && teamMembers.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">No team members added yet.</p>
                  )}

                  {!loadingMembers && teamMembers.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {teamMembers.map((m) => (
                        <div
                          key={m.teamMemberId}
                          className={`rounded-xl border p-4 ${m.isActive ? "border-gray-200 bg-white" : "border-gray-200 bg-gray-50 opacity-70"}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                                {(m.name || "?").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                                <p className="text-xs text-gray-500">{m.designation}</p>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
                              {m.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>

                          <p className="text-xs text-gray-500 mt-3 flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5" />
                            {m.email}
                          </p>

                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                            <div>
                              <p className="text-xs text-gray-500">Open tickets</p>
                              <p className={`text-lg font-bold ${m.activeTicketCount > 3 ? "text-amber-600" : "text-gray-900"}`}>
                                {m.activeTicketCount}
                              </p>
                            </div>
                            <button
                              onClick={() => toggleMemberActive(m)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                                m.isActive
                                  ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                                  : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                              }`}
                            >
                              {m.isActive ? "Deactivate" : "Activate"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Assign Modal */}
      {assignModalTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-white">Assign Ticket</h3>
                <p className="text-xs text-blue-100">{assignModalTicket.ticketNumber} - {assignModalTicket.subject}</p>
              </div>
              <button onClick={() => setAssignModalTicket(null)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">Choose a team member. Open ticket counts are shown so you can see who's free.</p>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {activeMembers.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No active team members. Add one first.</p>
                )}
                {activeMembers.map((m) => (
                  <label
                    key={m.teamMemberId}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer transition ${
                      String(m.teamMemberId) === assignTargetId ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="assignTarget"
                        value={m.teamMemberId}
                        checked={String(m.teamMemberId) === assignTargetId}
                        onChange={(e) => setAssignTargetId(e.target.value)}
                        className="text-blue-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{m.name}</p>
                        <p className="text-xs text-gray-500">{m.designation}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${m.activeTicketCount > 3 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
                      {m.activeTicketCount} open
                    </span>
                  </label>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setAssignModalTicket(null)}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssign}
                  disabled={assigning}
                  className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium hover:from-blue-700 hover:to-indigo-700 shadow-sm disabled:opacity-50 transition"
                >
                  {assigning ? "Assigning..." : "Assign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
